import { Link } from "@/lib/nav";

import CustomerFooter from "@/components/customer-footer";
import CustomerScreenShell from "@/components/customer-screen-shell";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { intakeFromPublicPath } from "@buildpy/core/modules/customer/intake-page";
import { AREA_BAND_LABEL, RESIDENCE_LABEL } from "@buildpy/core/modules/customer/types";
import { pairAltOf } from "@buildpy/core/modules/portfolio/pairs";
import { publicPortfolioPath } from "@buildpy/core/modules/portfolio/public-view";
import PublicNotice from "@/modules/portfolio/ui/public-notice";
import PublicPairSection, { PairStageLabel } from "@/modules/portfolio/ui/public-pair-section";
import PublicPhoto from "@/modules/portfolio/ui/public-photo";
import { publicPagePath } from "@buildpy/core/modules/public-page/rules";
import type { PublicPortfolioView } from "@buildpy/core/views/g4b/public-portfolio";

// 공개 시공사례 한 건 — 본문·사진. 비공개·남의 조직 id 는 목록과 같은 404(view 가 던진다).
const Page = ({ data }: { data: PublicPortfolioView }) => {
  if (data.kind === "notice") return <PublicNotice text={data.text} />;
  const { org, detail, show_inquiry, via } = data;
  const traits = [
    detail.area_band ? AREA_BAND_LABEL[detail.area_band] : null,
    detail.residence ? RESIDENCE_LABEL[detail.residence] : null,
  ].filter((v): v is string => v !== null);
  // 쌍에 든 장은 쌍 섹션에서만 보이고, 나머지만 아래 나열에 남는다.
  const paired = new Set(detail.pairs.flatMap((pair) => [...pair.before, ...pair.after].map((image) => image.index)));
  const rest = detail.images.filter((_, index) => !paired.has(index));

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <header className="border-b border-border bg-card px-4 pb-3 pt-safe-3">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold">{org.name}</p>
          <div className="flex shrink-0 items-center gap-3">
            <Link
              className="text-sm text-muted-foreground underline-offset-2 hover:underline"
              data-testid="public-portfolio-back"
              href={via ? publicPagePath(via) : publicPortfolioPath(org.slug)}>
              시공사례 목록
            </Link>
            {show_inquiry && (
              <Button asChild size="sm">
                <Link data-testid="public-portfolio-inquiry" href={intakeFromPublicPath(org.slug, via, detail.id)}>
                  상담 문의
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <article className="mx-auto flex w-full max-w-3xl flex-col gap-y-4 px-4 pt-4" data-testid="public-portfolio-detail">
        {detail.cover_stage ? (
          <figure className="flex flex-col gap-y-2" data-testid="public-portfolio-cover">
            <PairStageLabel stage={detail.cover_stage} />
            <PublicPhoto alt={pairAltOf(detail.title, detail.cover_stage, 0, 1)} className="aspect-[4/3] w-full rounded-md" src={detail.cover_src} />
          </figure>
        ) : (
          <PublicPhoto alt={detail.title} className="aspect-[4/3] w-full rounded-md" src={detail.cover_src} />
        )}

        <div className="flex flex-col gap-y-1.5">
          <h1 className="text-xl font-semibold">{detail.title}</h1>
          {traits.length > 0 && <p className="text-sm text-muted-foreground">{traits.join(" · ")}</p>}
          {detail.tags.length > 0 && (
            <p className="flex flex-wrap gap-1">
              {detail.tags.map((tag) => (
                <Pill key={tag}>#{tag}</Pill>
              ))}
            </p>
          )}
        </div>

        {detail.summary && <p className="text-sm">{detail.summary}</p>}
        {detail.body && (
          <SectionCard as="div" className="whitespace-pre-line text-sm">{detail.body}</SectionCard>
        )}

        {detail.pairs.length > 0 && <PublicPairSection pairs={detail.pairs} />}

        {rest.length > 0 && (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="public-portfolio-images">
            {rest.map((image, i) => (
              <li className="flex flex-col gap-y-1" key={`${image.src}-${i}`}>
                <PublicPhoto
                  alt={image.caption || `${detail.title} 사진 ${i + 1}`}
                  className="aspect-square w-full rounded-md"
                  src={image.src}
                />
                {image.caption && <p className="text-xs text-muted-foreground">{image.caption}</p>}
              </li>
            ))}
          </ul>
        )}
      </article>
      <CustomerFooter className="px-4" show={data.footer} />
    </CustomerScreenShell>
  );
};

export default Page;
